import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, mergeProps as _mergeProps, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }

import { OrderTableContentViewModel } from './orderTableContentViewModel';
import { OrderHeaderKey, TableContentProperty } from '../../interface/tableContentHeaders';
import TableCell from '../TableCell.vue';
import OrderAlert from './OrderAlert.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';

import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import { TableContentItem } from '../../interface/tableContentItems';
import { EditableCell } from '../../interface/uiState';
import { isEditableCell } from '../../interface/uiState';
import EditableTableCell from '../EditableTableCell.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import OrderStatusReviseModal from '@/corePagesModule/components/OrderStatusReviseModal.vue';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { isOrder } from '@/coreDomainsModule/entities/order';
import { OrderService } from '@/orderTableModule/domains/service/orderService';
import { useReminderMailIcon } from '@/orderTableModule/composables/useReminderMailIcon';
import TableMemoButton from '@/corePagesModule/tableMemoButton/TableMemoButton.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'OrderTableCell',
  props: {
    header: {},
    orderHeaderKey: {},
    item: {},
    headerIndex: {},
    itemIndex: {},
    accumulationTableContentWidth: {},
    isVariableWidth: { type: Boolean },
    isAllSelected: { type: Boolean },
    stableColumnCount: {},
    isBold: { type: Boolean },
    isAlertOpening: { type: Boolean },
    editingId: {},
    isPartialDelivery: { type: Boolean },
    isChecked: { type: Boolean },
    isCheckedBusinessId: { type: Boolean },
    isOrderGroupEditMode: { type: Boolean }
  },
  emits: ["changeEditableValue", "onClickNarrow", "clickAlert", "onClickEdit", "onClickSave", "onClickDelete", "onClickSaveMemo", "onClickDeleteMemo", "onClickReminderMail", "onClickOrderBasis"],
  setup(__props: any, { emit: __emit }) {

const t = i18n.t;

const router = useRouter();

// Composables
const { getReminderMailIconName } = useReminderMailIcon();

// props
const props = __props;

// emits
const emits = __emit;

const orderTableContentVM = new OrderTableContentViewModel();

const orderReviseModalState = ref(false);

const changedOrderStatus = ref('');

/**
 * 編集中のセルかどうかを判定する
 * @param headerKey - ヘッダーキー
 */
const isEditing = (headerKey: OrderHeaderKey): headerKey is EditableCell => {
  return props.editingId === props.item.orderId && props.header.isEditable && isEditableCell(props.orderHeaderKey);
};

/**
 * 編集可能かどうかを判定する
 * @param headerKey - ヘッダーキー
 * @param item - テーブルのアイテム
 */
const isEditable = (headerKey: OrderHeaderKey, item: TableContentItem): boolean => {
  if (headerKey === 'OrderEstimateTimeOfArrival') {
    return OrderService.canChangeOrderDeliveryDate(item);
  }
  if (headerKey === 'OrderAmount') {
    return OrderService.canChangeOrderAmount(item);
  }
  return true;
};

/**
 * 編集状態でステータスを変更した際の処理
 * @param orderStatusString - ステータスの文字列
 */
const onConfirmedEditStatus = (orderStatusString: string) => {
  changedOrderStatus.value = orderStatusString;
  orderReviseModalState.value = false;
  emits('changeEditableValue', props.orderHeaderKey, orderStatusString);
};

const textAlign = computed(() => {
  return props.header.propertyPath?.propertyType === 'number' ? 'right' : 'left';
});

/**
 * アラート内の確認ボタンをクリックした際の処理
 * @param alertKind - アラートの種類
 */
const onClickCheckButtonInAlert = (alertKind: string) => {
  if (alertKind === 'orderAmountWarning') {
    // 詳細ページに移動する
    window.open(`#/business/recommend-order/${props.item.aggregateBusinessId}`, '_blank');
  }
};

/**
 * itemが発注か分納か判定して、displayOrderItemかdisplayPartialDeliveryItemを呼び出す
 * @param orderHeaderKey - ヘッダーキー
 * @param item - テーブルのアイテム
 */
const displayOrderItem = (orderHeaderKey: OrderHeaderKey, item: TableContentItem) => {
  return props.isPartialDelivery
    ? orderTableContentVM.displayPartialDeliveryItem(orderHeaderKey, item)
    : orderTableContentVM.displayOrderItem(orderHeaderKey, item);
};

/**
 * 保存ボタンをDisabledにするかどうか
 */
const isDisabledSaveButton = () => {
  if (isOrder(props.item)) {
    return !OrderService.checkOrderIntegrity(props.item);
  }
  return false;
};

/**
 * メモの表示内容を取得する
 * @param item - テーブルのアイテム
 */
const getMemoValue = (item: TableContentItem) => {
  const value = orderTableContentVM.displayOrderItem('OrderMemo', item);
  return value === '' ? undefined : value;
};

const isPaddingAvailCond = computed(() => {
  switch (props.header.key) {
    case 'OrderEditButton':
      return props.isOrderGroupEditMode;
    case 'OrderDeleteButton':
      return !props.editingId;
    default:
      return false;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(TableCell, {
      class: _normalizeClass([[
      _unref(orderTableContentVM).isSticky(_ctx.stableColumnCount, _ctx.headerIndex) ? 'sticky-content' : '',
      _ctx.orderHeaderKey === 'OrderDeleteButton' || isPaddingAvailCond.value ? 'no-padding' : '',
    ], "table-cell base-table__body--fixed"]),
      style: _normalizeStyle({
      left: _unref(orderTableContentVM).calcStickyDiff(_ctx.stableColumnCount, _ctx.headerIndex, _ctx.accumulationTableContentWidth),
    }),
      "text-align": textAlign.value,
      "table-content-property": _ctx.header,
      "tool-tip-value": _ctx.orderHeaderKey === 'OrderRecommendStatus' ? undefined : displayOrderItem(_ctx.orderHeaderKey, _ctx.item),
      "is-highlighted": props.isChecked || props.isCheckedBusinessId
    }, {
      default: _withCtx(() => [
        (_ctx.orderHeaderKey === 'OrderRecommendStatus')
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["order-recommend-status", { 'order-recommend-status--editable': isEditing(_ctx.orderHeaderKey) }]),
              onClick: _cache[0] || (_cache[0] = ($event: any) => (isEditing(_ctx.orderHeaderKey) ? (orderReviseModalState.value = true) : () => {}))
            }, [
              _createVNode(OrderStatusBadge, {
                "order-status-str": displayOrderItem(_ctx.orderHeaderKey, _ctx.item)
              }, null, 8, ["order-status-str"]),
              (isEditing(_ctx.orderHeaderKey))
                ? (_openBlock(), _createBlock(SvgLoader, {
                    key: 0,
                    name: "arrowDown"
                  }))
                : _createCommentVNode("", true)
            ], 2))
          : (_ctx.orderHeaderKey === 'OrderMemo')
            ? (_openBlock(), _createBlock(TableMemoButton, {
                key: 1,
                memo: getMemoValue(_ctx.item),
                onSave: _cache[1] || (_cache[1] = 
          (memoText) => {
            emits('onClickSaveMemo', _ctx.item.orderId, memoText);
          }
        ),
                onDelete: _cache[2] || (_cache[2] = 
          () => {
            emits('onClickDeleteMemo', _ctx.item.orderId);
          }
        )
              }, null, 8, ["memo"]))
            : (_ctx.orderHeaderKey === 'OrderAlert')
              ? (_openBlock(), _createBlock(OrderAlert, _mergeProps({ key: 2 }, { isOpening: props.isAlertOpening, ..._unref(orderTableContentVM).displayOrderAlert(_ctx.item) }, {
                  onClick: _cache[3] || (_cache[3] = 
          (isOpening) => {
            if (isOpening) emits('clickAlert');
          }
        ),
                  onOnNext: onClickCheckButtonInAlert
                }), null, 16))
              : (_ctx.orderHeaderKey === 'OrderEditButton')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                    (!props.isPartialDelivery && !props.isOrderGroupEditMode)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          (isEditing(_ctx.orderHeaderKey))
                            ? (_openBlock(), _createBlock(TextButton, {
                                key: 0,
                                "button-style": "btn-secondary",
                                size: "btn-ss",
                                disabled: isDisabledSaveButton(),
                                onClick: _cache[4] || (_cache[4] = () => emits('onClickSave', _ctx.item.orderId))
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)('保存')), 1)
                                ], undefined, true),
                                _: 1
                              }, 8, ["disabled"]))
                            : (props.editingId === '')
                              ? (_openBlock(), _createBlock(TextButton, {
                                  key: 1,
                                  "button-style": "btn-secondary",
                                  size: "btn-ss",
                                  onClick: _cache[5] || (_cache[5] = () => emits('onClickEdit', _ctx.item.orderId))
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(t)('編集')), 1)
                                  ], undefined, true),
                                  _: 1
                                }))
                              : _createCommentVNode("", true)
                        ], 64))
                      : _createCommentVNode("", true)
                  ], 64))
                : (_ctx.orderHeaderKey === 'ReminderMail')
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                      (!props.isPartialDelivery)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            class: "reminder-mail-button",
                            onClick: _cache[6] || (_cache[6] = () => emits('onClickReminderMail', _ctx.item.orderId))
                          }, [
                            _createVNode(SvgLoader, {
                              name: _unref(getReminderMailIconName)(_ctx.item.reminderMailDataId),
                              class: "reminder-mail-icon"
                            }, null, 8, ["name"])
                          ]))
                        : _createCommentVNode("", true)
                    ], 64))
                  : (_ctx.orderHeaderKey === 'OrderDeleteButton')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                        (isEditing(_ctx.orderHeaderKey) && _ctx.isPartialDelivery)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              class: "right-align",
                              onClick: _cache[7] || (_cache[7] = ($event: any) => (emits('onClickDelete', _ctx.item.id)))
                            }, [
                              _createVNode(SvgLoader, { name: "closeButtonIcon" })
                            ]))
                          : _createCommentVNode("", true)
                      ], 64))
                    : (_ctx.orderHeaderKey === 'OrderBasis')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
                          (_ctx.item.orderBasisId)
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 0,
                                class: "order-basis-button",
                                onClick: _cache[8] || (_cache[8] = ($event: any) => (emits('onClickOrderBasis', _ctx.item.orderId)))
                              }, [
                                _createVNode(SvgLoader, { name: "tableView" })
                              ]))
                            : _createCommentVNode("", true)
                        ], 64))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 7 }, [
                          (isEditing(_ctx.orderHeaderKey))
                            ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                                (isEditable(_ctx.orderHeaderKey, _ctx.item))
                                  ? (_openBlock(), _createBlock(EditableTableCell, {
                                      key: 0,
                                      "input-type": _ctx.header.propertyPath?.propertyType,
                                      "text-value": _ctx.orderHeaderKey === 'OrderPortEta' && _ctx.item.portEta == null ? '' : displayOrderItem(_ctx.orderHeaderKey, _ctx.item),
                                      onChangeDateValue: _cache[9] || (_cache[9] = ($event: any) => (emits('changeEditableValue', _ctx.orderHeaderKey, _unref(DateService).convertIDateToDate($event).toISOString()))),
                                      onChangeText: _cache[10] || (_cache[10] = ($event: any) => (emits('changeEditableValue', _ctx.orderHeaderKey, $event)))
                                    }, null, 8, ["input-type", "text-value"]))
                                  : (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(displayOrderItem(_ctx.orderHeaderKey, _ctx.item)), 1))
                              ]))
                            : (_openBlock(), _createElementBlock("span", {
                                key: 1,
                                class: _normalizeClass(["table-cell-content", { 'table-cell-content': _ctx.isBold }]),
                                onClick: _cache[11] || (_cache[11] = ($event: any) => (emits('onClickNarrow')))
                              }, _toDisplayString(displayOrderItem(_ctx.orderHeaderKey, _ctx.item)), 3))
                        ], 64))
      ], undefined, true),
      _: 1
    }, 8, ["class", "style", "text-align", "table-content-property", "tool-tip-value", "is-highlighted"]),
    _createVNode(OrderStatusReviseModal, {
      "modal-state": orderReviseModalState.value,
      "header-title": _unref(t)('ステータスの変更'),
      description: _unref(t)('発注のステータスを変更することができます。'),
      items: [_ctx.item],
      "is-partial-delivery": props.isPartialDelivery,
      onCanceled: _cache[12] || (_cache[12] = () => (orderReviseModalState.value = false)),
      onConfirm: _cache[13] || (_cache[13] = ($event: any) => (onConfirmedEditStatus($event.toString())))
    }, null, 8, ["modal-state", "header-title", "description", "items", "is-partial-delivery"])
  ], 64))
}
}

})